import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { GoAlert } from "react-icons/go";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import ListaCriticas from "./ListaCriticas";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";

const validationSchema = Yup.object().shape({
  sqTipoAutomacao: Yup.string().default(""),
  sgTipoAutomacao: Yup.string()
    .ensure()
    .default("")
    .required("Informe a Sigla")
    .max(200, "A sigla não pode ter mais de 200 caracteres"),
  dsTipoAutomacao: Yup.string().ensure().default("").required("Informe o nome do tipo de divergência"),
  listaCriticas: Yup.array().default([]),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (key === "listaCriticas") {
    initialValues[key] = [];
  } else {
    initialValues[key] = "";
  }
});

const FormTipoDivergencia = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [toggledClearRows, setToggleClearRows] = useState(false);
  const [dadosInfo, setDadosInfo] = useState({});

  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sqTipoAutomacao) submitRequest(apiRoutes.fiscalizacao.parametrizacao.alteraTipoDivergencia, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluiTipoDivergencia, values);
    },
    [submitRequest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid, errors } = formik;

  useEffect(() => {
    if ((submitData || submitErrors) && isSubmitting && !submitIsLoading) {
      setSubmitting(false);
    }

    if (submitErrors && isValid && !submitIsLoading) {
      setErrors({ ...errors, ...submitErrors });
    }
  }, [formik, setSubmitting, setErrors, errors, isSubmitting, isValid, submitData, submitErrors, submitIsLoading]);

  useEffect(() => {
    if (errors && isSubmitting) {
      if (errors.sgTipoAutomacao) {
        toast.warning(errors.sgTipoAutomacao);
      }
      if (errors.dsTipoAutomacao) {
        toast.warning(errors.dsTipoAutomacao);
      }
    }
  }, [errors, isSubmitting]);

  useEffect(() => {
    if (!submitData && !submitErrors) return;

    if (submitData) {
      toast.success("Tipo de Divergência salvo com sucesso");
      history.push("/app/tipodivergencia");
    }
  }, [submitData, submitErrors, history]);

  const {
    request: tipoDivergenciaRequest,
    data: tipoDivergenciaData,
    errors: tipoDivergenciaErrors,
    isLoading: tipoDivergenciaIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      const params = { id: state.itemKey };
      tipoDivergenciaRequest(apiRoutes.fiscalizacao.parametrizacao.consultaTipoDivergencia, params);
      delete state.itemKey;
    }
  }, [tipoDivergenciaRequest, state, setSubmitting]);

  useEffect(() => {
    if ((!tipoDivergenciaData && !tipoDivergenciaErrors) || tipoDivergenciaIsLoading) return;

    if (!formik.values.sqTipoAutomacao && tipoDivergenciaData && tipoDivergenciaData.result.length) {
      const info = {
        codigo: tipoDivergenciaData?.result[0]?.SqTipoAutomacao,
        data: {
          nomeFormalInclusao: tipoDivergenciaData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: tipoDivergenciaData?.result[0]?.nomeFormalAlteracao,
          documentoInclusao: tipoDivergenciaData?.result[0]?.usuarioInclusao,
          documentoAlteracao: tipoDivergenciaData?.result[0]?.usuarioAlteracao,
          dataInclusao: tipoDivergenciaData?.result[0]?.dataInclusao,
          dataAlteracao: tipoDivergenciaData?.result[0]?.dataAlteracao,
        },
      };

      formik.setValues({ ...tipoDivergenciaData.result[0] });
      setDadosInfo(info);
    }

    if (tipoDivergenciaErrors) {
      toast.error(tipoDivergenciaErrors.message);
    }
  }, [formik, tipoDivergenciaData, tipoDivergenciaErrors, tipoDivergenciaIsLoading]);

  const handleClear = () => {
    formik.handleReset();
    setToggleClearRows(!toggledClearRows);
  };

  return (
    <>
      <h1 className="h4 mb-4 text-gray-800">Tipos de Divergência</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sqTipoAutomacao ? "Alteração de Tipo de Divergência" : "Novo Tipo de Divergência"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={submitData}
                errors={submitErrors}
                isLoading={submitIsLoading || tipoDivergenciaIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Tabs defaultActiveKey="geral" transition={false} id="tabTipDiv">
                  <Tab
                    eventKey="geral"
                    title={
                      <span>
                        <GoAlert
                          color="red"
                          size={15}
                          hidden={!(formik.errors.sgTipoAutomacao || formik.errors.dsTipoAutomacao)}
                        />{" "}
                        Dados Gerais
                      </span>
                    }
                  >
                    <fieldset
                      disabled={
                        formik.values.sqTipoAutomacao
                          ? !isAllowed(funcionalidades.tipoDivergencia.alterar)
                          : !isAllowed(funcionalidades.tipoDivergencia.inserir)
                      }
                    >
                      <Row>
                        <Form.Group as={Col} sm={12} md={6}>
                          <Form.Label>Sigla</Form.Label>
                          <TextInputFormik formik={formik} field="sgTipoAutomacao" maxLength="200" />
                        </Form.Group>
                        <Form.Group as={Col} sm={12} md={6}>
                          <Form.Label>Nome do Tipo de Divergência</Form.Label>
                          <TextInputFormik formik={formik} field="dsTipoAutomacao" maxLength="200" />
                        </Form.Group>
                      </Row>
                    </fieldset>
                  </Tab>
                  <Tab
                    eventKey="criticas"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.listaCriticas} /> Críticas
                      </span>
                    }
                    className="mb-4"
                  >
                    <ListaCriticas formik={formik} field="listaCriticas" toggledClearRows={toggledClearRows} />
                  </Tab>
                </Tabs>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Tipo de Divergência..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sqTipoAutomacao
                            ? funcionalidades.tipoDivergencia.alterar
                            : funcionalidades.tipoDivergencia.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                        className="btn btn-md"
                      >
                        Salvar
                      </ButtonSubmitFormikPermission>
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sqTipoAutomacao && (
                      <Button variant="light" className="btn btn-md mr-3" onClick={handleClear}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" className="btn btn-md" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormTipoDivergencia;
